import React, { createContext, useState, useCallback, useContext, useEffect } from "react";
import axios from "axios";
import config from '../config';
import { useNavigate } from "react-router-dom";

interface Samples {
    sampleId: number,
    identification: string,
    order: number,
    services: Services[]
}
interface Services {
    samplePackageId: number;
    serviceId: number;
    finalResult: number;
}
interface UpdatePayload {
    samplePackageId: number;
    sampleId: number;
    serviceId: number;
    oldResult: number;
    newResult: number;
    userName: string;
}

interface SamplesContextType {
    samples: Samples[] | null;
    token: string | null;
    error: string | null;
    loading: boolean;
    checkAuth: () => Promise<boolean>;
    setToken: (token: string | null) => void;
    setError: (error: string | null) => void;
    searchSamples: (first: string, last: string) => Promise<void>;
    updateSamples: (payload: UpdatePayload[]) => Promise<void>;
}
export const SamplesContext = createContext<SamplesContextType | null>(null);

export const SamplesProvider = ({ children }: { children: React.ReactNode }) => {
    const [samples, setSamples] = useState<Samples[] | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false); 
    const [error, setError] = useState<string | null>(null);
    const [lastSearchFirst, setLastSearchFirst] = useState('');
    const [lastSearchLast, setLastSearchLast] = useState('');
    const navigate = useNavigate();

    const checkAuth = useCallback(async (): Promise<boolean> => {
        if (!token) return false;
        try {
            const response = await axios.post(`${config.apiUrl}/validate`, { token });
            return response.status === 200;
        } catch (err) {
            setToken(null);
            navigate("/login");
            return false;
        }
    }, [token, navigate]);

    const searchSamples = useCallback(async (first: string, last: string): Promise<void> => {
        try {
            setLoading(true);
            setError(null);
            const response = await axios.get(`${config.aclUrl}/api/samples/results`, {
                params: {
                    firstSample: first, 
                    lastSample: last
                }
            });
            setSamples(response.data);
            setLastSearchFirst(first); // Salva o termo
            setLastSearchLast(last);
        } catch (err: any) {
            setError("Erro ao buscar dados");
            setSamples(null); 
        } finally {
            setLoading(false);
        }
    }, []);
    const updateSamples = async (payload: UpdatePayload[]) => {
        try {
            setLoading(true);
            setError(null);
            await axios.post(`${config.aclUrl}/api/samples/results/update`, payload);
            // log
            await axios.post(`${config.backendUrl}/api/logs/update-samples`, payload); 
            
            await searchSamples(lastSearchFirst, lastSearchLast);
        } catch (err) {
            setError("Erro ao atualizar resultados.");
            setSamples(null); 
        } finally {
            setLoading(false);
        }
    }

    return (
        <SamplesContext.Provider value={{
            samples, token, error, loading, 
            checkAuth, setToken, setError, searchSamples, updateSamples
        }}>
            {children}
        </SamplesContext.Provider>
    );
};

export const useSamples = () => {
    const context = useContext(SamplesContext);
    if (!context) {
        throw new Error("useSamples deve ser usado dentro de um samplesProvider");
    }
    return context;
};